"use client";

import Image from "next/image";
import Link from "next/link";
import { differenceInHours } from "date-fns";
import { Typography } from "../ui/typography";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ROUTES } from "@/constants/routes";
import { PdpPriceOffer, PdpPrices } from "@/types/pdp-prices";
import { AccommodationPriceOrigin } from "@/types/accommodation";
import { usePdpPrices } from "@/lib/react-query/queries/accommodation/use-pdp-prices";
import { ThreeBodyLoader } from "@/components/ui/three-body-loader";
import { formatNumber } from "@/utils/format/formatNumber";
import { formatRelativeUpdate } from "@/utils/format/formatRelativeUpdate";
import { usePdpStayDates } from "@/hooks/usePdpStayDates";
import { InfoTooltip } from "@/components/shared/InfoTooltip";
import { PdpReservedEmpty } from "./PdpReservedEmpty";

interface AccommodationPricesProps {
  accommodationId: string;
  /** PDP route params — the booking interstitial reloads the stay summary with them */
  stateCity: string;
  slug: string;
}

const STALE_PRICE_HOURS = 8;
const STALE_PRICE_TOOLTIP =
  "قیمت نهایی از سایت موردنظر دریافت نشد. لطفاً قیمت به‌روز را در سایت اصلی بررسی کنید.";

type OriginSnapshot = {
  hashId: string;
  origin?: AccommodationPriceOrigin | null;
  hostName?: string;
  baseCapacity?: number;
  extraCapacity?: number | null;
  updatedAt?: string;
};

const originName = (offer: OriginSnapshot): string =>
  offer.origin?.name || offer.hostName || "--";

const originLogo = (offer: OriginSnapshot): string | null =>
  offer.origin?.thumbnail || offer.origin?.logo || null;

const capacityText = (offer: {
  baseCapacity?: number;
  extraCapacity?: number | null;
}): string | null => {
  if (!offer.baseCapacity) return null;
  if (offer.extraCapacity) {
    return `${formatNumber(offer.baseCapacity)} نفر پایه + ${formatNumber(
      offer.extraCapacity,
    )} نفر مهمان`;
  }
  return `${formatNumber(offer.baseCapacity)} نفر پایه`;
};

const isPriceStale = (updatedAt?: string): boolean => {
  if (!updatedAt) return false;
  const updated = new Date(updatedAt);
  if (Number.isNaN(updated.getTime())) return false;
  return differenceInHours(new Date(), updated) >= STALE_PRICE_HOURS;
};

const isStayReserved = (data: PdpPrices): boolean =>
  data.supported.length === 0;

function OriginMark({ offer }: { offer: OriginSnapshot }) {
  const name = originName(offer);
  const logo = originLogo(offer);

  return (
    <div className="flex items-center gap-2">
      {logo && (
        <Image
          src={logo}
          width={34}
          height={34}
          alt={`${name} logo`}
          className="w-8.5 h-auto object-contain"
        />
      )}
      <Typography variant="bodyESRegular" className="md:text-sm">
        {name}
      </Typography>
    </div>
  );
}

function DisabledReserveButton() {
  return (
    <Button
      disabled
      className="mr-auto rounded-lg text-xs bg-gray-50 text-gray-500 hover:bg-gray-50"
    >
      مشاهده و رزرو
    </Button>
  );
}

function InactiveOriginCard({
  offer,
  updatedAt,
}: {
  offer: OriginSnapshot;
  updatedAt?: string | null;
}) {
  return (
    <div className="flex flex-col border border-gray-100 rounded-2xl p-4 gap-4">
      <div className="flex items-start gap-3">
        <div className="flex flex-col gap-2">
          <OriginMark offer={offer} />
          <Typography variant="captionMedium" color="gray900">
            اقامتگاه در این سایت غیرفعال است.
          </Typography>
          {updatedAt && (
            <Typography
              variant="captionMedium"
              color="gray400"
              className="text-[10px] md:text-xs"
            >
              {formatRelativeUpdate(updatedAt)}
            </Typography>
          )}
        </div>
        <Badge className="mr-auto rounded-full bg-[#D8D8D8] border-[#D8D8D8] text-[#333] font-normal">
          غیرفعال
        </Badge>
      </div>
      <DisabledReserveButton />
    </div>
  );
}

export const AccommodationPrices = ({
  accommodationId,
  stateCity,
  slug,
}: AccommodationPricesProps) => {
  const { checkIn, checkOut, capacity, nights } = usePdpStayDates();
  const { data, isPending } = usePdpPrices({
    accommodationId,
    checkIn,
    checkOut,
    capacity,
  });

  if (isPending) {
    return <ThreeBodyLoader />;
  }

  if (!data) {
    return <PdpReservedEmpty />;
  }

  if (isStayReserved(data)) {
    return <PdpReservedEmpty />;
  }

  const { supported, unsupported } = data;
  const stayNights = data.stayDates?.nights ?? nights;
  const lastScrapedAt = data.refresh?.lastScrapedAt;

  return (
    <div className="space-y-3">
      {supported.map((offer) => (
        <AvailableOriginCard
          key={offer.hashId}
          offer={offer}
          stayNights={stayNights}
          accommodationId={accommodationId}
          stateCity={stateCity}
          slug={slug}
          checkIn={checkIn}
          checkOut={checkOut}
          capacity={capacity}
        />
      ))}

      {unsupported.map((offer) => (
        <InactiveOriginCard
          key={offer.hashId}
          offer={offer}
          updatedAt={lastScrapedAt}
        />
      ))}
    </div>
  );
};

function AvailableOriginCard({
  offer,
  stayNights,
  accommodationId,
  stateCity,
  slug,
  checkIn,
  checkOut,
  capacity,
}: {
  offer: PdpPriceOffer;
  stayNights: number;
  accommodationId: string;
  stateCity: string;
  slug: string;
  checkIn: string;
  checkOut: string;
  capacity: number;
}) {
  const needsReview = isPriceStale(offer.updatedAt);
  const reserveHref = ROUTES.BOOKING_REDIRECT({
    offerId: offer.hashId,
    accommodationId,
    originName: offer.origin?.name,
    originLogo: offer.origin?.logo || offer.origin?.thumbnail || null,
    stateCity,
    slug,
    checkIn,
    checkOut,
    capacity,
  });

  return (
    <div className="flex flex-col border border-gray-100 rounded-lg p-4 gap-2 group">
      <div className="flex items-center gap-3">
        <OriginMark offer={offer} />
        <div className="flex items-center gap-2 mr-auto">
          <Typography variant="captionRegular" color="gray500">
            {formatNumber(stayNights)} شب/
          </Typography>
          <div
            className={`flex items-start flex-col ${
              offer.hasDiscount ? "-mb-6" : null
            }`}
          >
            {offer.hasDiscount && (
              <div className="flex items-center gap-2">
                <Typography
                  variant="bodySRegular"
                  color="black200"
                  className="line-through"
                >
                  {formatNumber(offer.price)}
                </Typography>
                <Badge
                  dir="ltr"
                  className="!bg-[#F9EBED] border-[#F9EBED] text-error-500 rounded-[5px] text-xs font-normal h-5"
                >
                  % {formatNumber(offer.discountPercent)}
                </Badge>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Typography variant="bodySMedium" className="md:text-base">
                {formatNumber(offer.discountedPrice)}
              </Typography>
              <Typography variant="captionRegular">تومان</Typography>
            </div>
          </div>
        </div>
      </div>

      <Typography variant="captionMedium" color="gray900">
        {capacityText(offer)}
      </Typography>

      {offer.extraCapacityPrice > 0 && (
        <Typography
          variant="captionMedium"
          color="gray900"
          className="md:max-h-0 md:-mb-2 group-hover:mb-0 overflow-hidden group-hover:max-h-6 transition-all duration-300 group-hover:delay-0 delay-75"
        >
          ● هر نفر اضافه: {formatNumber(offer.extraCapacityPrice)} تومان
        </Typography>
      )}

      {offer.updatedAt && (
        <div className="flex items-center gap-2">
          {needsReview && <InfoTooltip content={STALE_PRICE_TOOLTIP} />}
          <Typography
            variant="captionMedium"
            color="gray400"
            className="text-[10px]"
          >
            {formatRelativeUpdate(offer.updatedAt)}
          </Typography>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {needsReview ? (
          <Badge className="rounded-full bg-[#FAE0C2]/70 border-transparent text-[#EB8B25] font-normal">
            نیازمند بررسی
          </Badge>
        ) : (
          <>
            {offer.hasDiscount && <Badge variant="gray">تخفیف ویژه</Badge>}
            {offer.isInstantBookable && <Badge variant="gray">رزرو فوری</Badge>}
          </>
        )}
        <Button
          asChild
          variant={needsReview ? "outline" : "default"}
          className={
            needsReview
              ? "mr-auto rounded-lg text-xs border-primary text-primary"
              : "mr-auto rounded-lg text-xs"
          }
        >
          <Link href={reserveHref} target="_blank" rel="noopener noreferrer">
            مشاهده و رزرو
          </Link>
        </Button>
      </div>
    </div>
  );
}
